import { useRef, useEffect, useState, Dispatch, SetStateAction } from 'react';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormHeader } from '../FormHeader';
import {
  TextField,
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  useMediaQuery,
  Theme,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import CopyToClipboard from 'react-copy-to-clipboard';
import DictationButton from './DictationButton';
import { NotesGeneratorClearDialog } from '../../../../components/Dialogs';

import { selectUser } from 'store/user';
import { ApiUser } from 'types';
import { Spinner } from 'components';
import { BackButton } from 'components';

import styles from './NotesGenerator.module.scss';

type NotesGeneratorProps = {
  onBackToSettingsClick(): void;
};

type NotesGeneratorFormType = {
  description: string;
  visitType: string;
  noteLength: string;
  noteType: string;
};

function correctGrammarLocally(text: string): string {
  if (!text) return text;

  // Convert spoken punctuation into actual punctuation but only at the end of a sentence
  text = text.replace(/\b(period|question mark|exclamation|exclamation point)\b(?=\s*$)/gi, (match) => {
    switch (match.toLowerCase()) {
      case 'period':
        return '.';
      case 'question mark':
        return '?';
      case 'exclamation':
      case 'exclamation point':
        return '!';
      default:
        return match;
    }
  });

  // Capitalize the first letter of each sentence
  text = text.replace(/(^|[.!?]\s+)([a-z])/g, (_, prefix, letter) => {
    return `${prefix}${letter.toUpperCase()}`;
  });

  text = text.replace(
    /(.*)(\b[\w\s]+)(?:,\s*|\s+)([\w\s]+?)\s(and|or)\s([\w\s]+?)([.!?])$/,
    (match, beforeText, item1, item2, conjunction, item3, punctuation) => {
      return `${beforeText}${item1}, ${item2}, ${conjunction} ${item3}${punctuation}`;
    },
  );

  text = text.replace(
    /\b(who|what|when|where|why|how|is|are|did|does|do|should|could|would|will|can|was|were)\b\s+([a-z].*?)([.!?]?)\s*$/gi,
    (match, questionWord, restOfSentence, endPunctuation) => {
      // Fix: Ensure the detected question is a **complete standalone sentence**
      const fullSentence = `${questionWord} ${restOfSentence}`.trim();
      if (
        fullSentence.includes(' but ') || // "but" means it's not a question
        fullSentence.includes(' and ') || // "and" likely means it's part of a statement
        fullSentence.includes(' that ') || // "that" means it's dependent
        !/^\s*[A-Z]/.test(fullSentence) // Ensure the sentence starts with an uppercase letter (not a fragment)
      ) {
        return match; // ✅ Skip false positives
      }

      const finalPunctuation = endPunctuation.trim();
      if (finalPunctuation === '?' || finalPunctuation === '!') {
        return `${fullSentence}${finalPunctuation} `;
      }

      return `${fullSentence}? `;
    },
  );

  // Ensure sentence-ending punctuation, but only if it doesn't already end with punctuation
  text = text.replace(/([^.!?])(\s*)$/, (_, lastChar, spaces) => {
    return `${lastChar.trim()}.`; // Always ensure the sentence ends properly
  });

  // Remove an extra period if a sentence already ends in `?` or `!`
  text = text.replace(/([!?])\.$/, '$1');

  // Remove unnecessary spaces before punctuation
  text = text.replace(/\s+([.,!?])/g, '$1');

  return text.trim();
}

export default function NotesGenerator({ onBackToSettingsClick }: NotesGeneratorProps) {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const user: ApiUser = useSelector(selectUser)!;
  const aRef = useRef<HTMLAnchorElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [aiResponse, setAiResponse] = useState<string>('');
  const [editable, setEditable] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');

  const MAX_CREDITS = 100;
  const [credits, setCredits] = useState(MAX_CREDITS);
  const [isDisabled, setIsDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isClearDialogOpen, setIsClearDialogOpen] = useState<boolean>(false);
  const descriptionMirrorRef = useRef<HTMLTextAreaElement | null>(null);

  const handleClearClick = () => {
    setIsClearDialogOpen(true); // Open the confirmation dialog
  };

  const handleClearConfirm = (result: 'Confirmed' | 'Canceled') => {
    setIsClearDialogOpen(false); // Close the dialog

    if (result === 'Confirmed') {
      setAiResponse(''); // Clear the AI response field
    }
  };

  const handleDictationUpdate: Dispatch<SetStateAction<string>> = (newTextOrUpdater) => {
    setDescription((prevText) => {
      const newText = typeof newTextOrUpdater === 'function' ? newTextOrUpdater(prevText) : newTextOrUpdater.trim();

      if (!newText) return prevText; // Prevent empty updates

      // Preserve punctuation when extracting new portions
      const newPortion = newText.startsWith(prevText) ? newText.slice(prevText.length).trim() : newText;

      if (!newPortion) {
        console.warn('Prevented duplication:', newText);
        return prevText; // Prevent duplicate additions
      }

      // Apply grammar correction to the new portion ONLY
      const correctedText = correctGrammarLocally(newPortion);

      // Avoid repeating the last few words (prevents loops)
      const prevWords = prevText.split(/\s+/).slice(-5).join(' ').toLowerCase();
      const newWords = correctedText.split(/\s+/).slice(0, 5).join(' ').toLowerCase();

      if (prevWords === newWords) {
        console.warn('Duplicate phrase detected:', correctedText);
        return prevText;
      }

      return `${prevText} ${correctedText}`.trim();
    });
  };

  useEffect(() => {
    // Set interval to clear console every 1 hour (3,600,000 milliseconds)
    const consoleClearInterval = setInterval(() => {
      console.clear();
      console.log('%cConsole cleared after 1 hour for performance reasons.', 'color: #ff9800; font-weight: bold;');
    }, 3600000); // 1 hour = 3,600,000 ms

    const handleUnload = () => {
      console.clear();
      console.log('%cConsole cleared on page unload.', 'color: #ff9800; font-weight: bold;');
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(consoleClearInterval);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);

  useEffect(() => {
    const storedCredits = localStorage.getItem('note_credits');
    const storedMonth = localStorage.getItem('note_credits_month');
    const currentMonth = new Date().getMonth();

    const parsedMonth = storedMonth ? parseInt(storedMonth, 10) : -1;

    if (parsedMonth !== currentMonth) {
      localStorage.setItem('note_credits', JSON.stringify(MAX_CREDITS));
      localStorage.setItem('note_credits_month', JSON.stringify(currentMonth));
      setCredits(MAX_CREDITS);
      setIsDisabled(false);
    } else if (storedCredits !== null) {
      const parsedCredits = parseInt(storedCredits, 10);
      setCredits(parsedCredits);
      setIsDisabled(parsedCredits <= 0);
    } else {
      localStorage.setItem('note_credits', JSON.stringify(MAX_CREDITS));
      localStorage.setItem('note_credits_month', JSON.stringify(currentMonth));
      setCredits(MAX_CREDITS);
    }
  }, []);

  useEffect(() => {
    if (descriptionMirrorRef.current && description.length > 0) {
      descriptionMirrorRef.current.scrollTop = descriptionMirrorRef.current.scrollHeight;
    }
  }, [description]);

  const schema = Yup.object().shape({
    description: Yup.string()
      .max(2500, 'Max length is 2,500 characters')
      .min(10, 'Min length is 10 characters')
      .required(),
    visitType: Yup.string().required('Visit Type is required'),
    noteLength: Yup.string().required('Note Length is required'),
    noteType: Yup.string().required('Note Type is required'),
  });

  const form = useForm<NotesGeneratorFormType>({
    resolver: yupResolver(schema),
    defaultValues: {
      description: '',
      visitType: localStorage.getItem('last_visit_type') || '',
      noteLength: localStorage.getItem('last_note_length') || '',
      noteType: localStorage.getItem('last_note_type') || '',
    },
    mode: 'onBlur',
  });

  const { watch } = form;
  const visitType = watch('visitType');
  const noteLength = watch('noteLength');
  const noteType = watch('noteType');

  // Fix TypeScript: Extend `window` to include `SpeechRecognition`
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

  const [isListening, setIsListening] = useState<boolean>(false);

  const retrieveAIGeneratedResponse = async (taskId: string, token: string): Promise<void> => {
    try {
      const responseData: any = null;

      while (!responseData) {
        await new Promise((resolve) => setTimeout(resolve, 2000)); // wait 2s

        const response = await fetch(
          `${process.env['REACT_APP_HIPAA_LINK_API_URI']}api/ainotes/lemur-response/${taskId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`❌ Error fetching AI response. Status: ${response.status}`);
        }

        const result = await response.json();

        if (result.generated_note) {
          const rawNote = result.generated_note.trim();

          const formattedNote = rawNote
            .replace(/^([A-Z])\s*\((.*?)\):/gm, (_: string, letter: string, full: string) => `**${full.trim()}**:`) // Bold headers like S (Subjective)
            .replace(/^- /gm, '• ') // Markdown dash to bullet
            .replace(/([^\n])\n(- )/g, '$1\n\n$2') // ✅ Ensure proper newline before new bullets
            .replace(/\n{2,}/g, '\n\n') // Normalize spacing
            .trim();

          setAiResponse(formattedNote);
          setEditable(true);

          const updatedCredits = credits - 1;
          setCredits(updatedCredits);
          localStorage.setItem('note_credits', JSON.stringify(updatedCredits));

          return;
        }

        // Optional: prevent infinite loop if failed for too long
        console.warn('⚠️ Still waiting for AI-generated note...');
      }
    } catch (error) {
      console.error('⚠️ Error retrieving AI response:', error);
      enqueueSnackbar(error instanceof Error ? error.message : 'An error occurred.', { variant: 'error' });
      setErrorMessage(error instanceof Error ? error.message : 'An error occurred.');
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!description.trim() || credits <= 0) return;
    setLoading(true);
    setErrorMessage('');

    try {
      const token = JSON.parse(localStorage.getItem('tokens') || '{}').token;

      if (!token) {
        console.error('⚠️ Authentication token missing in localStorage.');
        enqueueSnackbar('⚠️ Authentication token missing. Please log in again.', { variant: 'error' });
        return;
      }

      localStorage.setItem('last_visit_type', form.getValues().visitType);
      localStorage.setItem('last_note_length', form.getValues().noteLength);
      localStorage.setItem('last_note_type', form.getValues().noteType);

      const tokenLimitMapping: Record<string, number> = {
        Concise: 300,
        Standard: 500,
        Detailed: 750,
      };

      const tokenLimit = tokenLimitMapping[form.getValues().noteLength] || 500;

      const requestBody = {
        session_type: form.getValues().visitType,
        note_type: form.getValues().noteType,
        length: form.getValues().noteLength,
        token_limit: tokenLimit,
        raw_notes: description.trim(),
      };

      const response = await fetch(`${process.env['REACT_APP_HIPAA_LINK_API_URI']}api/ainotes/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`❌ Failed to create AI Note. Status: ${response.status}`);
      }

      const taskData = await response.json();

      if (!taskData.request_id) {
        throw new Error('❌ Missing request_id in AI response.');
      }

      const taskId = taskData.request_id;

      await retrieveAIGeneratedResponse(taskId, token); // Backend handles AI calls
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '⚠️ An unknown error occurred.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setErrorMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={styles.container}>
      {isMobile && (
        <Box sx={{ padding: '0.5rem 0' }}>
          <BackButton text='Back to Settings' onClick={onBackToSettingsClick} />
        </Box>
      )}
      {loading && <Spinner />}
      <div className={styles.formHeaderWrapper}>
        <FormHeader
          items={[
            {
              text: (
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 700,
                      fontSize: '1.5rem',
                    }}
                  >
                    NOTES GENERATOR
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#81CFDD',
                      color: '#ffffff',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      padding: '0.1rem 0.4rem',
                      borderRadius: '5px',
                      textTransform: 'uppercase',
                    }}
                  >
                    beta
                  </Box>
                </Stack>
              ),
            },
          ]}
          rightSlot={
            <Stack sx={{ paddingRight: '2rem', textAlign: 'right' }}>
              <Typography
                variant='body1'
                sx={{
                  color: credits === 0 ? 'red' : '#777',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                Free Credits: {credits}/100
              </Typography>
              <Typography
                variant='body6'
                sx={{
                  color: '#777',
                  fontSize: '0.85rem',
                  fontStyle: 'italic',
                }}
              >
                *resets monthly
              </Typography>
            </Stack>
          }
        />
      </div>

      <Box className={styles.main}>
        <Typography variant='body2'>{'Generate AI-assisted clinical notes based on session details.'}</Typography>

        <div className={styles.innerContainer}>
          {/* Drop-Downs Section */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.25, sm: 2 }}
            sx={{ width: '100%', marginBottom: 2 }}
          >
            {/* Visit Type */}
            <Stack sx={{ width: '100%' }}>
              <Typography variant='body2' fontWeight='bold' sx={{ marginBottom: '0.5rem' }}>
                Visit Type <span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormControl fullWidth sx={{ marginBottom: { xs: '2rem', sm: 0 } }}>
                <Select
                  value={form.watch('visitType') || ''}
                  onChange={(e) => form.setValue('visitType', e.target.value)}
                  displayEmpty
                  renderValue={(selected) =>
                    !selected ? <span className={styles.placeholder}>- Select Type -</span> : selected
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 6.5, // ~6 items visible before scroll
                      },
                    },
                  }}
                >
                  <MenuItem value='Follow-Up'>Follow-Up</MenuItem>
                  <MenuItem value='Consultation'>Consultation</MenuItem>
                  <MenuItem value='Intake'>Intake</MenuItem>
                  <MenuItem value='Discharge'>Discharge</MenuItem>
                  <MenuItem value='Last/Termination'>Last/Termination</MenuItem>
                  <MenuItem value='Couples Counseling'>Couples Counseling</MenuItem>
                  <MenuItem value='Family Therapy'>Family Therapy</MenuItem>
                  <MenuItem value='Child Therapy'>Child Therapy</MenuItem>
                  <MenuItem value='Group Therapy'>Group Therapy</MenuItem>
                  <MenuItem value='Crisis Session'>Crisis Session</MenuItem>
                  <MenuItem value='Case Management'>Case Management</MenuItem>
                  <MenuItem value='Medication Management'>Medication Management</MenuItem>
                  <MenuItem value='Treatment Planning'>Treatment Planning</MenuItem>
                  <MenuItem value='Supportive Counseling'>Supportive Counseling</MenuItem>
                  <MenuItem value='Psychoeducation'>Psychoeducation</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Note Length */}
            <Stack sx={{ width: '100%' }}>
              <Typography variant='body2' fontWeight='bold' sx={{ marginBottom: '0.5rem' }}>
                Note Length <span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormControl fullWidth sx={{ marginBottom: { xs: '2rem', sm: 0 } }}>
                <Select
                  value={form.watch('noteLength') || ''}
                  onChange={(e) => form.setValue('noteLength', e.target.value)}
                  displayEmpty
                  renderValue={(selected) =>
                    !selected ? <span className={styles.placeholder}>- Select Length -</span> : selected
                  }
                >
                  <MenuItem value='Concise'>Concise</MenuItem>
                  <MenuItem value='Standard'>Standard</MenuItem>
                  <MenuItem value='Detailed'>Detailed</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {/* Note Type */}
            <Stack sx={{ width: '100%' }}>
              <Typography variant='body2' fontWeight='bold' sx={{ marginBottom: '0.5rem' }}>
                Note Type <span style={{ color: 'red' }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={form.watch('noteType') || ''}
                  onChange={(e) => form.setValue('noteType', e.target.value)}
                  displayEmpty
                  renderValue={(selected) =>
                    !selected ? <span className={styles.placeholder}>- Select Note -</span> : selected
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 6.5, // ~6 items visible before scroll
                      },
                    },
                  }}
                >
                  <MenuItem value='SOAP'>SOAP</MenuItem>
                  <MenuItem value='DAP'>DAP</MenuItem>
                  <MenuItem value='BIRP'>BIRP</MenuItem>
                  <MenuItem value='GIRP'>GIRP</MenuItem>
                  <MenuItem value='PIRP'>PIRP</MenuItem>
                  <MenuItem value='SIRP'>SIRP</MenuItem>
                  <MenuItem value='PIE'>PIE</MenuItem>
                  <MenuItem value='Narrative'>Narrative</MenuItem>
                  <MenuItem value='Progress'>Progress</MenuItem>
                  <MenuItem value='EMDR'>EMDR</MenuItem>
                  <MenuItem value='IEP'>IEP</MenuItem>
                  <MenuItem value='ICAN'>ICAN</MenuItem>
                  <MenuItem value='H&P'>H&P</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {/* Description and Response Fields */}
          <Typography variant='body' fontWeight='bold' sx={{ marginBottom: '0rem' }}>
            Description
          </Typography>
          <Typography variant={isMobile ? 'body5' : 'body2'} sx={{ marginTop: '-1rem' }}>
            Provide a brief summary of the session, highlighting key details and observations.
          </Typography>
          <TextField
            multiline
            fullWidth
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);

              // 🛑 Auto-stop dictation if user types
              const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
              const activeRecognition = (window as any)
                .activeRecognitionInstance as typeof window.webkitSpeechRecognition;

              if (activeRecognition) {
                console.log('🛑 Typing detected — stopping dictation');
                activeRecognition.stop();
                (window as any).activeRecognitionInstance = null;
                setIsListening(false); // ✅ update UI

                if (typeof (window as any).resetDictationMemory === 'function') {
                  (window as any).resetDictationMemory();
                }
              }
            }}
            placeholder={
              description.length === 0
                ? 'Example: Client reports increased work stress, frequent worry, and occasional panic episodes. Struggles with sleep and racing thoughts. Expresses frustration with difficulty relaxing. Discussed deep breathing, mindfulness, and anxiety management. Pt engaged, verbalized understanding, and plans to implement coping strategies. Will reassess progress next session.'
                : ''
            }
            rows={4}
            inputProps={{
              maxLength: 2500,
              style: { lineHeight: '1.9rem' },
            }}
            inputRef={descriptionMirrorRef}
            className='descriptionField'
            InputProps={{
              sx: {
                padding: 0,
                minHeight: '100px',
                overflow: 'auto',
                '& textarea': {
                  lineHeight: '1.9rem',
                  fontSize: '1.4rem',
                  padding: '10px',
                },
                '& textarea::placeholder': {
                  color: '#aaa',
                  opacity: 1,
                },
              },
            }}
          />

          <Stack direction='row' alignItems='center' sx={{ marginTop: '-.1rem', justifyContent: 'space-between' }}>
            {/* Left-Aligned Submit Button with Dictation Button Right Next to It */}
            <Stack direction='row' alignItems='center'>
              <Button
                variant='contained'
                onClick={handleSubmit}
                disabled={!visitType || !noteLength || !noteType || description.length < 10}
                sx={{ minWidth: '120px' }}
              >
                Submit
              </Button>

              {/* Dictation Button - Speech to Text */}
              {/* <DictationButton
                setDescription={handleDictationUpdate}
                isListening={isListening}
                setIsListening={setIsListening}
                descriptionMirrorRef={descriptionMirrorRef}
              /> */}
            </Stack>

            {/* Right-Aligned Character Counter */}
            <Typography variant='body1' sx={{ color: '#777', paddingRight: '0.2em' }}>
              {description.length.toLocaleString()}/2,500
            </Typography>
          </Stack>

          <Typography variant='body' fontWeight='bold' sx={{ marginTop: 2 }}>
            Response
          </Typography>

          <Box
            sx={{
              border: '1px solid #ccc',
              padding: '1rem',
              borderRadius: '4px',
              backgroundColor: aiResponse ? '#fff' : '#f5f5f5',
              minHeight: '150px',
              fontSize: '1.4rem',
              lineHeight: '1.9rem',
              whiteSpace: 'pre-wrap',
              overflow: 'auto',
            }}
          >
            {aiResponse ? (
              <Typography
                variant='body2'
                component='div'
                sx={{ whiteSpace: 'normal', lineHeight: '1.9rem', fontSize: '1.4rem' }}
                dangerouslySetInnerHTML={{
                  __html: aiResponse
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Convert **bold** to <strong>
                    .replace(/\n/g, '<br/>'), // Convert newlines to <br/>
                }}
              />
            ) : (
              <Typography variant='body2' sx={{ color: '#aaa' }}>
                AI-generated response will appear here...
              </Typography>
            )}
          </Box>

          <Stack direction='row' spacing={1} sx={{ marginTop: '-.1rem' }}>
            <CopyToClipboard
              text={aiResponse}
              onCopy={() => enqueueSnackbar('Copied to clipboard!', { variant: 'success' })}
            >
              <Button variant='outlined' disabled={!aiResponse}>
                Copy
              </Button>
            </CopyToClipboard>
            <Button variant='outlined' disabled={!aiResponse} onClick={handleClearClick}>
              Clear
            </Button>
          </Stack>
        </div>
      </Box>

      <Typography
        variant='body2'
        sx={{
          marginTop: 3,
          marginBottom: 2,
          color: '#777',
          fontSize: '1.2rem',
          fontStyle: 'italic',
          textAlign: 'center',
        }}
      >
        ⚠️ Please review all generated content for accuracy.
      </Typography>

      <NotesGeneratorClearDialog
        open={isClearDialogOpen}
        onClose={() => setIsClearDialogOpen(false)}
        onClearConfirmResult={handleClearConfirm}
      />
    </Box>
  );
}

export { NotesGenerator };
